import { safeFetch } from "../utils/safeFetch";

// ─── CLIENTS ───────────────────────────────────────────

export const getAllClienti = async (token, tenant, { noCache = false } = {}) => {
  const response = await safeFetch(`/api/clients`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, noCache ? null : 'allClients');

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getOrdiniByCliente = async (clientId, token, tenant) => {
  const response = await safeFetch(`/api/orders/by-client/${clientId}`, { // ← fixed URL
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, `clientOrders_${clientId}_${Date.now()}`); // ← cache busted

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getOrdiniByCompany = async (companyId, token, tenant) => {
  const response = await safeFetch(`/api/orders/by-company/${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, `companyOrders_${companyId}_${Date.now()}`);

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getOrdiniByHeliteRoom = async (roomNumber, token, tenant) => {
  const normalizedRoom = String(roomNumber || '').trim();
  const response = await safeFetch(`/api/orders/by-helite-room/${encodeURIComponent(normalizedRoom)}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, `heliteRoomOrders_${normalizedRoom}_${Date.now()}`);

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const creaCliente = async (data, token, tenant) => {
  const response = await safeFetch(`/api/clients`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      "x-tenant-id": tenant,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    const errorBody = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorBody.error ?? response.statusText);
  }

  return response.json();
};

export const updateClient = async (clientId, data, token, tenant) => {
  const response = await safeFetch(`/api/clients/${clientId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    const errorBody = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(errorBody.error ?? errorBody.message ?? `HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

// ─── COMPANIES ─────────────────────────────────────────

export const getAllCompanies = async (token, tenant) => {
  const response = await safeFetch(`/api/companies`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, 'allCompanies');

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const createCompany = async (company, token, tenant) => {
  const response = await safeFetch(`/api/companies`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(company),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const getCompanyById = async (companyId, token, tenant) => {
  const response = await safeFetch(`/api/companies/${companyId}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, `company_${companyId}`);

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const updateCompany = async (companyId, data, token, tenant) => {
  const response = await safeFetch(`/api/companies/${companyId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};

export const deleteCompany = async (companyId, token, tenant) => {
  const response = await safeFetch(`/api/companies/${companyId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      'x-tenant-id': tenant,
    },
  });

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.status === 204 ? null : await response.json();
};

export const getCompanyClients = async (companyId, token, tenant) => {
  const response = await safeFetch(`/api/companies/${companyId}/clients`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'x-tenant-id': tenant
    }
  }, `companyClients_${companyId}`);

  if (!response.ok) {
    if (response.status === 0) throw new Error("Impossibile connettersi al server o recuperare dati offline.");
    else throw new Error(`HTTP error! status: ${response.status}`);
  }

  return await response.json();
};
